import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "react-toastify/dist/ReactToastify.css";
import ProductCard from "../ProductCard/ProductCard";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { adaptRentalProduct } from "../../utils/rentalProductAdapter";
import { trackEvent } from "../../utils/analytics";

const getApiBase = () => {
  return (
    process.env.REACT_APP_RENTAL_API_BASE ||
    "http://localhost:3111"
  ).replace(/\/$/, "");
};

const getCart = () => JSON.parse(localStorage.getItem("rentalCart")) || [];

const isProductInCart = (productId, cartItems) =>
  cartItems.some((item) => item.id === productId);

const MostPopular = () => {
  const [mostPopular, setMostPopular] = useState([]);
  const [selectedQuantities] = useState({});
  const [cartItems, setCartItems] = useState(getCart());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const fetchMostPopular = async () => {
    setLoading(true);
    setLoadError("");

    try {
      const API_BASE = getApiBase();

      const response = await fetch(`${API_BASE}/api/products/most-popular`);

      if (!response.ok) {
        throw new Error(`Laitteiden haku epäonnistui: ${response.status}`);
      }

      const data = await response.json();

      if (data.ok && Array.isArray(data.items)) {
        const adapted = data.items
          .map(adaptRentalProduct)
          .filter(
            (device) =>
              device.name &&
              Number.isFinite(device.price)
          );

        setMostPopular(adapted);

        trackEvent("suosituimmat_ladattu", {
          tuotteiden_maara: adapted.length,
          sijainti: "etusivu_mostpopular",
        });
      } else {
        console.warn("most-popular ei palauttanut items-taulukkoa", data);
        setMostPopular([]);
        setLoadError("Suosituimpia tuotteita ei voitu näyttää juuri nyt.");
      }
    } catch (error) {
      console.error("Virhe suosituimpien haussa:", error);
      setMostPopular([]);
      setLoadError("Suosituimpia tuotteita ei voitu näyttää juuri nyt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init({ duration: 700, easing: "ease-in-out", once: true });
    fetchMostPopular();

    const handleCartUpdate = () => setCartItems(getCart());
    window.addEventListener("cartUpdated", handleCartUpdate);

    return () =>
      window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  return (
    <section
      className="
        relative overflow-hidden py-20
        bg-gradient-to-br from-white via-[#F3FFFC] to-[#E9FBF7]
        dark:from-[#050505] dark:via-[#08080A] dark:to-[#0D0D12]
      "
      aria-labelledby="most-popular-heading"
      data-aos="fade-up"
    >
      <div className="mx-auto max-w-7xl px-4">
        <div
          className="
            rounded-[2rem] border border-[#DDE7E5] bg-white p-6 shadow-xl
            dark:border-darkborder dark:bg-[#111114]
            md:p-8
          "
        >
          <div className="mb-10 max-w-4xl">
            <span
              className="
                mb-4 inline-flex rounded-full border border-primary/40 bg-primary/10
                px-4 py-2 font-customFont1 text-sm font-extrabold uppercase
                tracking-[0.14em] !text-[#111827]
                dark:!text-white
              "
            >
              Suosituimmat
            </span>

            <h2
              id="most-popular-heading"
              className="
                font-customFont2 text-left text-3xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-snug
                text-text
                dark:text-darktext
              "
            >
              Vuokratuimmat
            </h2>

            <p
              className="
                mt-5 max-w-3xl font-customFont1 text-left text-lg font-normal
                leading-relaxed !text-[#374151]
                dark:!text-[#D1D5DB]
                xl:text-xl
              "
            >
              Tutustu Spiderlegs Productionsin{" "}
              <strong className="font-bold !text-[#111827] dark:!text-white">
                suosituimpiin vuokrattaviin laitteisiin
              </strong>{" "}
              äänentoistoon, valaistukseen ja esitystekniikkaan.
            </p>
          </div>

          {loading && (
            <div
              className="
                rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC] p-6
                font-customFont1 text-base font-semibold !text-[#374151]
                dark:border-darkborder dark:bg-darksurface dark:!text-[#D1D5DB]
              "
            >
              Ladataan suosituimpia tuotteita...
            </div>
          )}

          {!loading && loadError && (
            <div
              className="
                rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC] p-6
                font-customFont1 text-base font-semibold !text-[#374151]
                dark:border-darkborder dark:bg-darksurface dark:!text-[#D1D5DB]
              "
            >
              {loadError}
            </div>
          )}

          {!loading && !loadError && mostPopular.length > 0 && (
            <div className="flex justify-center">
              <div
                className="
                  grid w-full grid-cols-1 gap-6
                  sm:grid-cols-2 lg:grid-cols-3
                  2xl:grid-cols-4
                "
              >
                {mostPopular.slice(0, 6).map((item, index) => {
                  const disableAddButton =
                    item.onko_polaroid && isProductInCart(item.id, cartItems)
                      ? false
                      : false;

                  return (
                    <div
                      key={item.id}
                      className="
                        w-full transition hover:-translate-y-1
                      "
                      onMouseEnter={() => {
                        trackEvent("suosittu_tuote_hover", {
                          tuote_id: item.id,
                          tuote_nimi: item.name,
                          sijainti: "etusivu_mostpopular",
                          jarjestys: index + 1,
                        });
                      }}
                    >
                      <ProductCard
                        item={item}
                        cartItems={cartItems}
                        selectedQuantities={selectedQuantities}
                        isDisabled={disableAddButton}
                        allItems={mostPopular}
                        hideCartButtons={true}
                        hideQuantitySelector={true}
                        hideAvailableCount={true}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-10 grid place-content-center">
            <Link
              to="/vuokrattavat"
              onClick={() => {
                trackEvent("siirtyi_vuokrattaviin", {
                  lahto: "etusivu_mostpopular",
                });
                window.scrollTo({ top: 0, left: 0, behavior: "instant" });
              }}
              aria-label="Siirry katsomaan kaikki vuokrattavat tuotteet"
            >
              <ResponsiveButton variant="seemore">
                Katso kaikki
              </ResponsiveButton>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MostPopular;